import * as THREE from "three";

const LOOK_SENSITIVITY = 0.0042;
const MIN_PITCH = -1.42;
const MAX_PITCH = 1.42;
const MIN_FOV = 28;
const MAX_FOV = 78;
const BASE_SPEED = 18;
const SPRINT = 2.6;

export class FlyCamera {
  readonly camera: THREE.PerspectiveCamera;
  yaw = 0.18;
  pitch = -0.32;

  private keys = new Set<string>();
  private dragging = false;
  private canvas: HTMLCanvasElement;
  private velocity = new THREE.Vector3();

  constructor(canvas: HTMLCanvasElement, camera: THREE.PerspectiveCamera) {
    this.canvas = canvas;
    this.camera = camera;
    this.camera.rotation.order = "YXZ";
    this.yaw = 0.08;
    this.pitch = -0.3;
    this.camera.position.set(6, 15, 46);
    this.applyRotation();
    this.bind();
  }

  private bind(): void {
    const c = this.canvas;

    c.addEventListener("pointerdown", (e) => {
      if (e.button !== 0) return;
      this.dragging = true;
      c.classList.add("is-dragging");
      c.setPointerCapture(e.pointerId);
    });

    c.addEventListener("pointerup", (e) => {
      this.dragging = false;
      c.classList.remove("is-dragging");
      if (c.hasPointerCapture(e.pointerId)) c.releasePointerCapture(e.pointerId);
    });

    c.addEventListener("pointermove", (e) => {
      if (!this.dragging) return;
      this.yaw -= e.movementX * LOOK_SENSITIVITY;
      this.pitch -= e.movementY * LOOK_SENSITIVITY;
      this.pitch = Math.min(MAX_PITCH, Math.max(MIN_PITCH, this.pitch));
      this.applyRotation();
    });

    c.addEventListener(
      "wheel",
      (e) => {
        e.preventDefault();
        const next = this.camera.fov + Math.sign(e.deltaY) * 2.4;
        this.camera.fov = Math.min(MAX_FOV, Math.max(MIN_FOV, next));
        this.camera.updateProjectionMatrix();
      },
      { passive: false },
    );

    c.addEventListener("contextmenu", (e) => e.preventDefault());

    window.addEventListener("keydown", (e) => {
      this.keys.add(e.code);
    });
    window.addEventListener("keyup", (e) => {
      this.keys.delete(e.code);
    });
    window.addEventListener("blur", () => this.keys.clear());
  }

  private applyRotation(): void {
    this.camera.rotation.set(this.pitch, this.yaw, 0, "YXZ");
  }

  update(dt: number): void {
    const sprint = this.keys.has("ShiftLeft") || this.keys.has("ShiftRight");
    const speed = BASE_SPEED * (sprint ? SPRINT : 1);

    const forward = this.keys.has("KeyW") || this.keys.has("ArrowUp") ? 1 : 0;
    const back = this.keys.has("KeyS") || this.keys.has("ArrowDown") ? 1 : 0;
    const right = this.keys.has("KeyD") || this.keys.has("ArrowRight") ? 1 : 0;
    const left = this.keys.has("KeyA") || this.keys.has("ArrowLeft") ? 1 : 0;
    const up = this.keys.has("KeyE") || this.keys.has("Space") ? 1 : 0;
    const down = this.keys.has("KeyQ") || this.keys.has("ControlLeft") ? 1 : 0;

    this.velocity.set(right - left, up - down, back - forward);
    if (this.velocity.lengthSq() > 0) {
      this.velocity.normalize().multiplyScalar(speed * dt);
      this.camera.translateX(this.velocity.x);
      this.camera.translateY(this.velocity.y);
      this.camera.translateZ(this.velocity.z);
    }
  }
}
